import { createTheme } from '@mui/material/styles';

// Constantes de cores centralizadas para facilitar manutenção
export const COLORS = {
  // Material Design 3 Surface Colors
  surface: '#0f0f0f',
  surfaceContainer: '#1e1e1e',
  surfaceContainerHigh: '#2a2a2a',
  surfaceContainerLow: '#1a1a1a',
  surfaceContainerHighest: '#363636',

  // Material Design 3 On Colors
  onSurface: '#e6e1e5',
  onSurfaceVariant: '#d4d0db', // Cor mais clara para melhor contraste
  onPrimary: '#FFFFFF',

  // Material Design 3 Outline Colors
  outline: '#938f99',
  outlineVariant: '#444746',

  // Material Design 3 Container Colors
  primaryContainer: '#1A202C',

  // Tibia Theme Colors
  tibiaYellow: '#ffff4d',

  // Material Design 3 Palette Colors
  primaryDark: '#1A202C',
  secondaryLight: '#63B3ED',
  secondaryDark: '#3182CE',
  success: '#10B981',
  error: '#EF4444',
  warning: '#F59E0B',
  info: '#3B82F6',

  // Legacy colors (mantidos para compatibilidade)
  primary: '#2d2d2d',
  secondary: '#4299E1',
};

// Tema customizado inspirado no Tibia com Material Design 3
export const tibiaTheme = createTheme({
  cssVariables: true, // Habilita CSS Variables do Material 3
  palette: {
    mode: 'dark',
    primary: {
      main: COLORS.primary,
      light: COLORS.primary,
      dark: COLORS.primaryDark,
      contrastText: COLORS.onPrimary,
    },
    secondary: {
      main: COLORS.secondary,
      light: COLORS.secondaryLight,
      dark: COLORS.secondaryDark,
      contrastText: COLORS.onPrimary,
    },
    background: {
      default: COLORS.surface,
      paper: COLORS.surfaceContainer,
    },
    text: {
      primary: COLORS.onSurface,
      secondary: COLORS.onSurfaceVariant,
    },
    success: {
      main: COLORS.success,
    },
    error: {
      main: COLORS.error,
    },
    warning: {
      main: COLORS.warning,
    },
    info: {
      main: COLORS.info,
    },
    divider: COLORS.outlineVariant,
    // Material 3 color tokens
    surface: {
      main: COLORS.surface,
      variant: COLORS.surfaceContainer,
    },
    surfaceContainer: {
      main: COLORS.surfaceContainer,
      high: COLORS.surfaceContainerHigh,
      highest: COLORS.surfaceContainerHighest,
      low: COLORS.surfaceContainerLow,
    },
    onSurface: {
      main: COLORS.onSurface,
      variant: COLORS.onSurfaceVariant,
    },
    outline: {
      main: COLORS.outline,
      variant: COLORS.outlineVariant,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Arial", sans-serif',
    // Material Design 3 Typography Scale
    displayLarge: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '3.5rem',
      fontWeight: 400,
      lineHeight: 1.15,
      letterSpacing: 0,
    },
    displayMedium: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '2.8125rem',
      fontWeight: 400,
      lineHeight: 1.15,
      letterSpacing: 0,
    },
    displaySmall: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '2.25rem',
      fontWeight: 400,
      lineHeight: 1.15,
      letterSpacing: 0,
    },
    headlineLarge: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '2rem',
      fontWeight: 400,
      lineHeight: 1.25,
      letterSpacing: 0,
    },
    headlineMedium: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '1.75rem',
      fontWeight: 400,
      lineHeight: 1.25,
      letterSpacing: 0,
    },
    headlineSmall: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '1.5rem',
      fontWeight: 400,
      lineHeight: 1.25,
      letterSpacing: 0,
    },
    titleLarge: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '1.375rem',
      fontWeight: 500,
      lineHeight: 1.25,
      letterSpacing: 0,
    },
    titleMedium: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: 0.15,
    },
    titleSmall: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.43,
      letterSpacing: 0.1,
    },
    labelLarge: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.25,
      letterSpacing: 0.1,
    },
    labelMedium: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 1.33,
      letterSpacing: 0.5,
    },
    labelSmall: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '0.6875rem',
      fontWeight: 500,
      lineHeight: 1.45,
      letterSpacing: 0.5,
    },
    bodyLarge: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 0.15,
    },
    bodyMedium: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: 0.25,
    },
    bodySmall: {
      fontFamily: '"Roboto", "Arial", sans-serif',
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.33,
      letterSpacing: 0.4,
    },
    // Mapeamento para compatibilidade com MUI
    h1: {
      fontWeight: 400,
      fontSize: '3.5rem',
      lineHeight: 1.15,
      letterSpacing: 0,
      color: COLORS.onSurface,
    },
    h2: {
      fontWeight: 400,
      fontSize: '2.8125rem',
      lineHeight: 1.15,
      letterSpacing: 0,
      color: COLORS.onSurface,
    },
    h3: {
      fontWeight: 400,
      fontSize: '2.25rem',
      lineHeight: 1.15,
      letterSpacing: 0,
      color: COLORS.onSurface,
    },
    h4: {
      fontWeight: 400,
      fontSize: '2rem',
      lineHeight: 1.25,
      letterSpacing: 0,
      color: COLORS.onSurface,
    },
    h5: {
      fontWeight: 400,
      fontSize: '1.75rem',
      lineHeight: 1.25,
      letterSpacing: 0,
      color: COLORS.onSurface,
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.375rem',
      lineHeight: 1.25,
      letterSpacing: 0,
      color: COLORS.onSurface,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 0.15,
      color: COLORS.onSurface,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: 0.25,
      color: COLORS.onSurfaceVariant,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: `${COLORS.surfaceContainer} !important`,
          boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)', // elevation 1
          borderBottom: `1px solid ${COLORS.outlineVariant}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: COLORS.surfaceContainer,
          borderRight: `1px solid ${COLORS.outlineVariant}`,
          boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)', // elevation 2
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.surfaceContainer,
          border: `1px solid ${COLORS.outlineVariant}`,
          borderRadius: '12px', // Material 3 shape
          boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)', // elevation 1
        },
        elevation1: {
          boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)',
        },
        elevation2: {
          boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 1px 10px 0px rgba(0, 0, 0, 0.08), 0px 2px 4px -1px rgba(0, 0, 0, 0.2)',
        },
        elevation3: {
          boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.12), 0px 1px 14px 0px rgba(0, 0, 0, 0.08), 0px 3px 4px -2px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '20px', // Material 3 full shape
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          lineHeight: 1.25,
          letterSpacing: 0.1,
          padding: '8px 24px',
          minHeight: '40px',
          border: `1px solid ${COLORS.outline}`,
          backgroundColor: COLORS.surfaceContainer,
          color: COLORS.onSurface,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: COLORS.surfaceContainerHigh,
            borderColor: COLORS.onSurfaceVariant,
            boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)',
          },
          '&:active': {
            boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)',
          },
        },
        contained: {
          backgroundColor: COLORS.primary,
          color: COLORS.onPrimary,
          border: 'none',
          '&:hover': {
            backgroundColor: COLORS.primaryContainer,
            boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 1px 10px 0px rgba(0, 0, 0, 0.08)',
          },
        },
        outlined: {
          borderColor: COLORS.outline,
          color: COLORS.onSurface,
          '&:hover': {
            backgroundColor: `rgba(144, 143, 153, 0.08)`,
            borderColor: COLORS.onSurfaceVariant,
          },
        },
        text: {
          border: 'none',
          backgroundColor: 'transparent',
          color: COLORS.onSurface,
          '&:hover': {
            backgroundColor: `rgba(230, 225, 229, 0.08)`,
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: `rgba(230, 225, 229, 0.08)`,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          padding: '8px 16px',
          color: COLORS.onSurface,
          '&:hover': {
            backgroundColor: `rgba(230, 225, 229, 0.08)`,
          },
          '&.Mui-selected': {
            backgroundColor: `rgba(230, 225, 229, 0.12)`,
            color: COLORS.primary,
            '&:hover': {
              backgroundColor: `rgba(230, 225, 229, 0.16)`,
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.surfaceContainer,
          border: `1px solid ${COLORS.outlineVariant}`,
          borderRadius: '12px',
          boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: COLORS.surfaceContainerHighest,
            borderRadius: '12px',
            '& fieldset': {
              borderColor: COLORS.outline,
            },
            '&:hover fieldset': {
              borderColor: COLORS.onSurfaceVariant,
            },
            '&.Mui-focused fieldset': {
              borderColor: COLORS.primary,
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: COLORS.onSurfaceVariant,
            '&.Mui-focused': {
              color: COLORS.primary,
            },
          },
          '& .MuiOutlinedInput-input': {
            color: COLORS.onSurface,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: COLORS.outlineVariant,
          height: '1px',
          margin: '16px 0',
          '&::before, &::after': {
            borderColor: COLORS.outlineVariant,
          },
        },
      },
    },
  },
});